import { styled } from '@linaria/react';
import { Flex, Group } from '@mantine/core';
import * as Sentry from '@sentry/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  type DotProps,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ModelComparisonFamily,
  ModelComparisonResponse,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { LineChartLegendItemWithCheckbox } from '../../../components/line-chart/line-chart-legend-item-with-checkbox';
import { inkLight, skyLight } from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';
import { StyledButton } from '../../financials/charts/shared/chart-display-type-button-set';
import { LineChartDot } from '../../process-analysis/components/line-chart/line-chart-dot';
import { LineChartTooltip } from '../../process-analysis/components/line-chart/line-chart-tooltip';
import { hasDataForItem } from '../../process-analysis/components/line-chart/line-chart.helpers';
import type { ChartDataItem, LineItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { useCycleView } from './model-comparison.hooks';
import { buildChartData, buildItemColors, buildLineItems } from './model-comparison.helpers';
import type { SessionsDrilldownWindow } from './model-comparison.type';

type Props = {
  response: ModelComparisonResponse;
  /**
   * The full, provider-filter-unaware family list, used only to seed stable
   * color assignment (see the comment on `itemColors` below) — `response`
   * itself may already be narrowed by the provider filter.
   */
  allFamilies: ModelComparisonFamily[];
  /**
   * Opens the sessions side panel for the clicked point's model family,
   * narrowed to the period that point represents. Note the point's value is a
   * median *task* cycle time, so the drill-down lists the family's sessions
   * active in that period rather than the exact tasks behind the median.
   */
  onSelectFamily?: (modelFamily: string, window?: SessionsDrilldownWindow) => void;
  /**
   * Controlled visibility: families whose lines are hidden. When provided
   * (together with onToggleFamily), visibility state lives with the caller —
   * the filter panel's select/deselect-all shares it via the URL. When
   * omitted, the chart falls back to its own local state.
   */
  hiddenFamilies?: Set<string>;
  /** Controlled-mode legend toggle handler; pairs with hiddenFamilies. */
  onToggleFamily?: (family: string) => void;
};

const formatCycleTime = (value: number | null) => (value !== null && value !== undefined ? `${value}d` : '—');

type ChartLegendProps = {
  items: LineItem[];
  chartData: ChartDataItem[];
  hiddenIds: Set<string>;
  hoveredItem: string | null;
  itemColors: Record<string, string>;
  onToggle: (id: string) => void;
  setHoveredItem: (item: string | null) => void;
};

/**
 * Legend row showing each model family that has a point in the current chart,
 * with a checkbox to toggle its line on/off and the existing
 * hover-to-highlight behavior. The checkbox itself is colored to match its
 * line — no separate color dot next to the label — mirroring the Flora "Flow
 * by Phase" artifact's legend (`flora-agent/artifacts/flow-by-phase/flow-by-phase-artifact.tsx`).
 *
 * @param {ChartLegendProps} props - Component props
 * @returns {JSX.Element} The legend row
 */
export const ChartLegend = ({
  items,
  chartData,
  hiddenIds,
  hoveredItem,
  itemColors,
  onToggle,
  setHoveredItem,
}: ChartLegendProps) => {
  try {
    const sortedItems = items
      .filter((item) => hasDataForItem(chartData, item.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Flex justify="center" align="center" gap="md" wrap="wrap" h="100%">
        {sortedItems.map((item) => {
          return (
            <LineChartLegendItemWithCheckbox
              key={item.id}
              value={item.id}
              label={item.name}
              // Model-family names ARE the comparison axis, so don't clip them
              // at the shared 12-char default; only a pathologically long raw
              // model id truncates (with the full name still on hover).
              maxLabelLength={40}
              color={itemColors[item.id]}
              // `undefined` when nothing is hovered → the shared component keeps
              // every item at full opacity; otherwise only the hovered item.
              isHovered={hoveredItem === null ? undefined : hoveredItem === item.id}
              isChecked={!hiddenIds.has(item.id)}
              onMouseEnter={setHoveredItem}
              onMouseLeave={() => setHoveredItem(null)}
              onToggle={onToggle}
            />
          );
        })}
      </Flex>
    );
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Median task cycle time per period by model family: one line per family, with
 * a checkbox next to each legend entry to show/hide that family's line. All
 * families always appear in the legend — checked/unchecked is local, chart-
 * only presentation state, independent of the provider filter that narrows
 * which families are fetched/shown at all. Periods without completions are
 * bridged (connectNulls) so each line stays continuous. The period size
 * (daily…monthly) follows the sidebar's time-allocation filter, defaulting to
 * a window-derived bucket.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The chart card content
 */
export const CycleTimeChart = ({ response, allFamilies, onSelectFamily, hiddenFamilies, onToggleFamily }: Props) => {
  const { t } = useTranslation();
  const { cycleView, setCycleView } = useCycleView();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  // Uncontrolled fallback only — when hiddenFamilies/onToggleFamily are
  // supplied, visibility is controlled by the caller (URL-backed, shared with
  // the filter panel's select/deselect-all control).
  const [internalHiddenIds, setInternalHiddenIds] = useState<Set<string>>(new Set());
  const hiddenIds = hiddenFamilies ?? internalHiddenIds;

  const lineItems = useMemo(() => buildLineItems(response.families), [response.families]);
  const chartData = useMemo(() => buildChartData(response), [response]);
  // Colors are assigned by index over `allFamilies` — the provider-filter-
  // UNAWARE full list — never over `lineItems` (which reflects the current
  // provider selection). A family's color is fixed by its slot in the full
  // list, so hiding families or narrowing the provider filter never repaints
  // the ones still shown.
  const itemColors = useMemo(() => buildItemColors(buildLineItems(allFamilies)), [allFamilies]);

  const toggleItem = (id: string) => {
    if (onToggleFamily) {
      onToggleFamily(id);
      return;
    }
    setInternalHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleItems = lineItems.filter((item) => !hiddenIds.has(item.id));
  const isHighlighted = (itemId: string) => hoveredItem === null || hoveredItem === itemId;

  return (
    <ChartLayout data-testid="model-comparison-cycle-chart">
      <Header>
        <TitleBlock>
          <H3>{t('agent_miner.model_comparison.cycle_chart_title')}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.model_comparison.cycle_chart_subtitle')}
          </Text>
        </TitleBlock>
        <Group gap="xs" data-testid="cycle-view-toggle">
          <StyledButton
            label={t('agent_miner.model_comparison.toggle_line')}
            iconName="stacked_line_chart"
            selected={cycleView === 'line'}
            onClick={() => setCycleView('line')}
          />
          <StyledButton
            label={t('agent_miner.model_comparison.toggle_bar')}
            iconName="bar_chart"
            selected={cycleView === 'bar'}
            onClick={() => setCycleView('bar')}
          />
        </Group>
      </Header>
      <ChartArea>
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 10, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              label={{
                value: t('agent_miner.model_comparison.cycle_axis_label'),
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
            <RechartsTooltip content={<LineChartTooltip formatValue={formatCycleTime} />} />
            {visibleItems.map((item) => {
              // The family comes from the closure (the series id IS the
              // family); the week comes from the clicked point's own row, so
              // the drill-down is scoped to the period that dot represents
              // rather than the whole selected window. Both `dot` and
              // `activeDot` need the handler — the active dot is what's
              // rendered under the cursor, so it owns the real click.
              const handleDotClick = onSelectFamily
                ? (dotProps: DotProps) => {
                    // Uses `onPointSelect`, NOT `onClick`: Recharts rewrites
                    // event-named props on `activeDot` so the handler receives
                    // the element's declared props instead of the point's,
                    // which drops `payload` — and the active dot is what the
                    // user actually clicks. See LineChartDot for the details.
                    const { payload } = dotProps as DotProps & { payload?: ChartDataItem };
                    const periodStart = payload?.periodStart;
                    const periodEnd = payload?.periodEnd;
                    const window =
                      typeof periodStart === 'string' && typeof periodEnd === 'string'
                        ? { start: periodStart, end: periodEnd }
                        : undefined;
                    onSelectFamily(item.id, window);
                  }
                : undefined;
              return cycleView === 'bar' ? (
                <Bar
                  key={item.id}
                  dataKey={item.id}
                  name={item.name}
                  fill={itemColors[item.id]}
                  fillOpacity={isHighlighted(item.id) ? 1 : 0.3}
                  isAnimationActive={false}
                  cursor={onSelectFamily ? 'pointer' : undefined}
                  onClick={
                    onSelectFamily
                      ? (barData: { payload?: ChartDataItem }) => {
                          const periodStart = barData?.payload?.periodStart;
                          const periodEnd = barData?.payload?.periodEnd;
                          const window =
                            typeof periodStart === 'string' && typeof periodEnd === 'string'
                              ? { start: periodStart, end: periodEnd }
                              : undefined;
                          onSelectFamily(item.id, window);
                        }
                      : undefined
                  }
                />
              ) : (
                <Line
                  key={item.id}
                  type="monotone"
                  dataKey={item.id}
                  name={item.name}
                  stroke={itemColors[item.id]}
                  strokeWidth={2}
                  strokeOpacity={isHighlighted(item.id) ? 1 : 0.3}
                  dot={
                    <LineChartDot
                      color={itemColors[item.id]}
                      opacity={isHighlighted(item.id) ? 1 : 0.3}
                      onPointSelect={handleDotClick}
                    />
                  }
                  activeDot={
                    <LineChartDot
                      color={itemColors[item.id]}
                      opacity={isHighlighted(item.id) ? 1 : 0.3}
                      onPointSelect={handleDotClick}
                    />
                  }
                  isAnimationActive={false}
                  // connectNulls: bridge periods with no completions so the
                  // trend line stays continuous across occasional empty
                  // periods rather than fragmenting into disconnected dots.
                  connectNulls
                />
              );
            })}
            <Legend
              content={
                <ChartLegend
                  items={lineItems}
                  chartData={chartData}
                  hiddenIds={hiddenIds}
                  hoveredItem={hoveredItem}
                  itemColors={itemColors}
                  onToggle={toggleItem}
                  setHoveredItem={setHoveredItem}
                />
              }
              verticalAlign="bottom"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartArea>
    </ChartLayout>
  );
};

const ChartLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* The chart area must own the remaining vertical space or the chart collapses
   to the ResponsiveContainer's 160px floor. */
const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;
